import { ComponentProps } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonLink from '../Input2/ButtonLink';

/**
 * Append `?templateDisplay=template` (or `&templateDisplay=template` if
 * the URL already has a query string) so the template page opens in
 * its template-display view instead of the editor. Idempotent —
 * doesn't add the param twice if it's already there.
 */
function appendTemplateDisplayQuery(href: string): string {
  if (href.includes('templateDisplay=')) return href;
  const sep = href.includes('?') ? '&' : '?';
  return `${href}${sep}templateDisplay=template`;
}

export type CampaignDayHeaderPureProps = ComponentProps<'div'> & {
  date: Date;
  now?: Date;
  /**
   * When true, render an explanatory tag on the right of the header
   * (e.g. "Not in template") so the user understands why the card is
   * tinted purple.
   */
  isDifferentFromTemplate?: boolean;
  /**
   * When set together with `isDifferentFromTemplate`, the "Not in
   * template" tag becomes a real link to this URL (e.g.
   * `/templates/{templateId}`) — clicking jumps to the template page so
   * the user can compare the divergence directly. When omitted the tag
   * stays a read-only badge.
   */
  templateHref?: string;
};

export function CampaignDayHeaderPure({
  date,
  now = new Date(),
  isDifferentFromTemplate,
  templateHref,
  className,
  ...props
}: CampaignDayHeaderPureProps) {
  const weekday = date.toLocaleString('en-US', { weekday: 'short' });
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  const isToday = date.toDateString() === now.toDateString();

  return (
    <div
      className={cn('flex items-baseline justify-between gap-2', className)}
      data-testid="campaign-day-header"
      {...props}>
      {/* Left group: date + Today badge sit together, with the badge
          immediately following the date. Right group: any explanatory
          tag (e.g. "Not in template") floats to the opposite edge. */}
      <div className="flex min-w-0 items-baseline gap-2">
        <span
          data-testid="campaign-day-header-date"
          className={cn(
            'text-xs font-semibold tracking-tight',
            // Today's date always reads green so the urgent day pops in a
            // long timeline regardless of the card's template-diff tint.
            isToday ? 'text-mantis-600 dark:text-mantis-400' : 'text-lynch-700 dark:text-lynch-300',
          )}>
          {weekday} · {month} {day}
        </span>
        {isToday && (
          <Button data-testid="campaign-day-header-today" size="2xs" color="green" tone="soft" readOnly>
            Today
          </Button>
        )}
      </div>
      {/*
        Right slot — always a real button-link (or nothing). The "Not in
        template" badge MUST be clickable: it takes the user to the
        cadence template page where they can see the canonical steps.
        If we ever land here without a templateHref, render NOTHING
        rather than a misleading non-clickable badge — a "not in
        template" affordance has no meaning when there's no template
        to navigate to.

        Priorities:
          - Day diverges + templateHref → purple "Not in template"
            ButtonLink jumping to /templates/{id}.
          - Day matches + templateHref → quiet "See in template" text
            link for reference.
          - No templateHref → render nothing.
      */}
      {isDifferentFromTemplate && templateHref != null && (
        <ButtonLink
          data-testid="campaign-day-header-not-in-template"
          size="2xs"
          color="purple" tone="soft"
          className="-mr-2"
          // Append `?templateDisplay=template` so the template page lands
          // in the template-display view (not the editor). Open in a new
          // tab so the user keeps the contact-detail page they were on.
          href={appendTemplateDisplayQuery(templateHref)}
          target="_blank"
          rel="noopener noreferrer"
          tooltip="Open the cadence template in a new tab — this day diverges from it.">
          Not in template
        </ButtonLink>
      )}
      {!isDifferentFromTemplate && templateHref != null && (
        <ButtonLink
          data-testid="campaign-day-header-see-in-template"
          size="2xs"
          color="gray"
          className="-mr-2"
          text
          href={appendTemplateDisplayQuery(templateHref)}
          target="_blank"
          rel="noopener noreferrer"
          tooltip="Open this day in the cadence template (opens a new tab).">
          See in template
        </ButtonLink>
      )}
    </div>
  );
}
